import {
  RESONANCE_CENTER_BAND_HZ,
  RESONANCE_LEVEL_FLOOR_DB,
} from '@muzly/shared';
import {
  VIBRATO_DEPTH_REFERENCE_CENTS,
  VIBRATO_REFERENCE_HZ,
  clampNum,
  vibratoBandScores,
} from '@muzly/voice-engine';

import type { DiagnosisRecord } from './record';

/**
 * resultCopy — 결과 화면(덱 p10)의 **판정 규칙**. 문구는 없다 — i18n 키 조각과 등급만 낸다.
 *
 * 화면에서 분리한 이유는 두 가지다. ① 이 규칙들이 전부 **미보정 임계값**이라 근거를 한자리에
 * 모아 놓아야 나중에 보정할 때 화면을 뒤지지 않는다. ② 미측정 축이 규칙에서 빠지는 방식
 * (`undefined`는 후보에서 제외 — 0으로 채우지 않는다)이 이 파일의 계약이고, 그건 렌더보다
 * 테스트로 지키는 게 싸다.
 *
 * React·IO 의존 없음 — vitest 대상.
 */

export type QualitativeLevel = 'low' | 'mid' | 'high';

/**
 * 목소리 맑기(HNR, dB) 구간. **미보정**이다.
 *
 * 근거: 정상 발성의 지속 모음 HNR은 임상에서 대체로 15~25dB로 보고되고, 10dB 아래는
 * 숨소리·거친 소리로 읽힌다 — 그 통념을 세 칸으로 옮긴 것이다. 다만 우리 값은 Praat이 아니라
 * 검출기가 이미 낸 주기성으로 `10·log10(r/(1−r))`을 계산한 것이라(pitch-core `voice_quality`)
 * 창·전처리가 다르다. 같은 목소리에서 임상 수치와 몇 dB 어긋날 수 있으므로 **점수로 바꾸지 않고
 * 정성 라벨로만** 쓰고, 화면은 미보정임을 함께 밝힌다.
 */
const CLARITY_DB = { mid: 10, high: 20 };

/**
 * 울림(2–4kHz 대역 에너지비, 0..1) 구간. **미보정**이다.
 *
 * 근거: 그 대역은 소리가 뚫고 나오는 느낌을 만드는 singer's formant 자리다(pitch-core가
 * 재는 대역). 편하게 말하듯 낸 소리에서는 총 에너지의 몇 %에 그치고, 울림을 실은 발성에서
 * 뚜렷하게 커진다 — 그 방향성만 세 칸으로 옮겼다. 절대값의 눈금은 기기 마이크 특성에 따라
 * 통째로 밀릴 수 있어 여기 숫자는 임시다.
 */
const RESONANCE_RATIO = { mid: 0.05, high: 0.2 };

/** 값 → 세 칸. 문턱 두 개가 칸을 가르는 유일한 규칙이다. */
const levelOf = (
  value: number,
  band: { mid: number; high: number },
): QualitativeLevel =>
  value >= band.high ? 'high' : value >= band.mid ? 'mid' : 'low';

const level = (
  value: number | undefined,
  band: { mid: number; high: number },
): QualitativeLevel | undefined =>
  // `0`은 값이다 — falsy로 묶으면 "쟀는데 0"이 "안 쟀다"로 둔갑한다.
  value == null ? undefined : levelOf(value, band);

export const clarityLevel = (hnrDb: number | undefined) =>
  level(hnrDb, CLARITY_DB);

export const resonanceLevel = (resonanceRatio: number | undefined) =>
  level(resonanceRatio, RESONANCE_RATIO);

/**
 * 최대발성지속시간 참고 밴드 — 덱 p10의 **단일 구간**(성별·연령 세분화는 데이터 확보 후,
 * 플랜 Decision). 임상 표(Kent 1987)는 폐기했다: 표시와 판정이 다른 자를 쓰면 화면이
 * "평균보다 짧다"면서 점수는 높게 주는 일이 생긴다.
 */
export const MPT_BAND = { minSec: 15, maxSec: 20 };

export type MptVerdict = 'short' | 'inBand' | 'long';

export const mptVerdict = (sustainSec: number): MptVerdict => {
  if (sustainSec < MPT_BAND.minSec) {
    return 'short';
  }
  return sustainSec > MPT_BAND.maxSec ? 'long' : 'inBand';
};

/**
 * 반음 폭 → "약 1옥타브 반" 꼴의 조각. 소수점(1.58옥타브)이 아니라 반 단위로 말하는 건
 * 덱 문구이자, 우리 음역이 그만한 정밀도를 갖지 않기 때문이다(퍼센타일로 뽑은 양끝).
 */
export const octaveSpan = (
  semitones: number,
): { key: 'whole' | 'half' | 'halfOnly'; octaves: number } => {
  const whole = Math.floor(semitones / 12);
  const rest = semitones - whole * 12;
  // 반올림 경계: 3반음 미만은 딱 떨어지는 옥타브로, 9반음 이상은 다음 옥타브로 읽는다.
  if (rest < 3) {
    return whole === 0
      ? { key: 'halfOnly', octaves: 0 }
      : { key: 'whole', octaves: whole };
  }
  if (rest >= 9) {
    return { key: 'whole', octaves: whole + 1 };
  }
  return whole === 0
    ? { key: 'halfOnly', octaves: 0 }
    : { key: 'half', octaves: whole };
};

const scoreOf = (record: DiagnosisRecord, key: string): number | undefined =>
  record.scores.find((s) => s.key === key)?.val;

/** 안정성 상·하 문턱(점수). 중간대는 강점도 개선점도 아니다 — 둘 다 아닌 칸이 있어야 한다. */
const STABILITY = { weak: 50, strong: 75 };
/** 음역 폭(반음) — 2옥타브 이상이면 넓다, 1옥타브 미만이면 좁다. */
const RANGE_SEMITONES = { narrow: 12, wide: 24 };
/** 노래 음정 정확도(%) 상·하 문턱. */
const SONG_ACCURACY = { weak: 60, strong: 80 };
/** 제때 들어간 비율. */
const ON_TIME_RATIO = { weak: 0.5, strong: 0.7 };

/**
 * 보컬 타입 문구의 **조각 키**(예: `['tone.high','stability.high','shape.balanced']`).
 * 덱의 조합 규칙 그대로 — 맑기 + 안정성 + 음역 폭. 화면이 이 조각들을 한 문장으로 잇는다
 * ("맑고 안정적인 밸런스형").
 *
 * **미측정 축은 조각이 통째로 빠진다.** 맑기를 안 쟀으면 "보통"으로 메우지 않는다 — 안 잰
 * 것을 중간값으로 채우면 그 사람의 타입이 아니라 우리 기본값을 말하게 된다. 낼 조각이 하나도
 * 없으면 `undefined`(화면은 "측정 준비 중").
 */
export const vocalTypeKeys = (
  record: DiagnosisRecord,
): string[] | undefined => {
  const parts: string[] = [];
  const tone = clarityLevel(record.hnrDb);
  if (tone) {
    parts.push(`tone.${tone}`);
  }
  const stability = level(scoreOf(record, 'pitchStability'), {
    mid: STABILITY.weak,
    high: STABILITY.strong,
  });
  if (stability) {
    parts.push(`stability.${stability}`);
  }
  if (parts.length === 0 && record.range == null) {
    return undefined;
  }
  const semitones = record.range?.semitones;
  if (semitones == null) {
    // 형용사만 남으면 문장이 안 끝난다 — 성부를 모를 때 쓸 중립 명사를 붙인다.
    return [...parts, 'shape.unknown'];
  }
  const shape =
    semitones >= RANGE_SEMITONES.wide
      ? 'wide'
      : semitones >= RANGE_SEMITONES.narrow
        ? 'balanced'
        : 'narrow';
  return [...parts, `shape.${shape}`];
};

/**
 * 박자 판정 — `undefined`는 **측정 불가**다(맞춘 음이 모자란 회차, songScoring.timing).
 * 문턱은 강점 판정과 **같은 상수**를 쓴다: 화면이 "안정적으로 따라갔어요"라고 하면서
 * 강점 목록에는 안 넣는 일이 생기면 한 화면이 자기 말을 뒤집는다.
 */
export const timingVerdict = (
  song: DiagnosisRecord['songScore'],
): 'steady' | 'late' | 'early' | undefined => {
  const timing = song?.timing;
  if (!timing) {
    return undefined;
  }
  if (timing.onTimeRatio >= ON_TIME_RATIO.strong) {
    return 'steady';
  }
  return timing.meanOffsetMs >= 0 ? 'late' : 'early';
};

/** 강점·개선점으로 낼 수 있는 항목 — 화면이 `points.<id>.<strong|weak>` 키로 옮긴다. */
export type ResultPointId =
  | 'sustain'
  | 'stability'
  | 'clarity'
  | 'resonance'
  | 'range'
  | 'register'
  | 'songAccuracy'
  | 'timing';

export interface ResultPoints {
  strengths: ResultPointId[];
  improvements: ResultPointId[];
}

/** 항목당 판정: 강점 / 개선점 / 둘 다 아님(중간대) / 후보 아님(미측정 → undefined). */
type Verdict = 'strong' | 'weak' | 'neither';

const verdictOf = (
  value: number | undefined,
  band: { weak: number; strong: number },
): Verdict | undefined => {
  if (value == null) {
    return undefined;
  }
  if (value >= band.strong) {
    return 'strong';
  }
  return value <= band.weak ? 'weak' : 'neither';
};

const levelVerdict = (
  value: QualitativeLevel | undefined,
): Verdict | undefined => {
  if (value == null) {
    return undefined;
  }
  return value === 'high' ? 'strong' : value === 'low' ? 'weak' : 'neither';
};

/** 화면에 내는 개수 상한(덱: 각 3가지). 모자라면 그만큼만 낸다 — 채우지 않는다. */
const MAX_POINTS = 3;

/** MPT 판정 → 강점/개선점. 밴드 안이면 둘 다 아니다. */
const MPT_VERDICT_POINT: Record<MptVerdict, Verdict> = {
  long: 'strong',
  short: 'weak',
  inBand: 'neither',
};

/**
 * 회차 값 → 강점·개선점(각 최대 3). 우선순위는 아래 배열 순서이고, **미측정 축은 후보에서
 * 아예 빠진다**(`undefined` → 건너뜀). 덱은 "진단별"이라 적었지만 화면이 내는 건 진단 셋을
 * 가로지른 강점 3 / 개선점 3이다 — 진단마다 3개씩이면 안 잰 축까지 채워야 한다.
 */
export const resultPoints = (record: DiagnosisRecord): ResultPoints => {
  const song = record.songScore;
  const candidates: [ResultPointId, Verdict | undefined][] = [
    // 호흡은 **화면 문구와 같은 판정**을 쓴다 — 여기서 문턱을 다시 매기면 20.0초에서
    // "평균 안에 들어요"라면서 강점에는 "한 호흡이 길게 이어져요"가 뜬다(자기 말 뒤집기).
    ['sustain', MPT_VERDICT_POINT[mptVerdict(record.sustainSec)]],
    ['stability', verdictOf(scoreOf(record, 'pitchStability'), STABILITY)],
    ['clarity', levelVerdict(clarityLevel(record.hnrDb))],
    ['resonance', levelVerdict(resonanceLevel(record.resonanceRatio))],
    [
      'range',
      // 한 옥타브(12반음)는 좁은 쪽이 아니다 — 그 **아래**부터 개선점이다.
      verdictOf(record.range?.semitones, {
        weak: RANGE_SEMITONES.narrow - 1,
        strong: RANGE_SEMITONES.wide,
      }),
    ],
    [
      'register',
      record.registerBreaks == null
        ? undefined
        : record.registerBreaks.length === 0
          ? 'strong'
          : 'weak',
    ],
    ['songAccuracy', verdictOf(song?.accuracy, SONG_ACCURACY)],
    ['timing', verdictOf(song?.timing?.onTimeRatio, ON_TIME_RATIO)],
  ];

  const pick = (want: Verdict): ResultPointId[] =>
    candidates
      .filter(([, verdict]) => verdict === want)
      .slice(0, MAX_POINTS)
      .map(([id]) => id);

  return { strengths: pick('strong'), improvements: pick('weak') };
};

/** 결과 상세 셸(시안 §8.2)이 탭으로 세우는 지표. 음역은 탭 밖(진단 2)에 그대로 있다. */
/**
 * 지표 탭 일곱 — **여기가 목록의 주인이다.** 상세 탭 바(`ResultTabs`)와 홈 타일
 * (`features/home/homeTiles`)이 둘 다 이 목록을 다 덮어야 한다: 한쪽에만 새 지표가
 * 생기면 홈에서 본 것을 상세에서 못 찾거나 그 반대가 된다.
 *
 * **순서는 여기 없다.** 시안이 두 화면에 다른 순서를 준다 — 상세 §8.2는 `음역`이
 * `음정`보다 앞이고 홈 §7.1은 반대다. 순서를 여기서 굳히면 한 화면이 시안과 어긋난다.
 */
export const METRIC_TAB_IDS = [
  'fatigue',
  'range',
  'registers',
  'vibrato',
  'breath',
  'formant',
  'resonance',
] as const;

export type MetricTabId = (typeof METRIC_TAB_IDS)[number];

/**
 * 공명 중심 주파수 미터의 **눈금**(Hz). 시안 §8.8은 `2k–2.8k–4k`로 그렸는데 그 자리의
 * 적정 범위도 `2~4kHz`라, 그대로 옮기면 마커가 적정 범위 밖에 설 수 없다 — 어떤 목소리든
 * "좋음"으로 읽히는 미터다(시안 결함 — SCREENS.md §8.8에 적어 뒀다).
 *
 * 그래서 눈금은 **엔진이 실제로 재는 대역**을 쓴다 — 숫자를 여기 다시 적지 않고
 * 프레임 계약이 들고 있는 `RESONANCE_CENTER_BAND_HZ`를 그대로 읽는다(정본은 pitch-core의
 * `kResonanceCenterLowHz`/`kResonanceCenterHighHz`). 그 밖에서는 값이 나올 수 없으니,
 * 미터 안의 위치는 잘림이 아니라 사실이다.
 */
export const FORMANT_CENTER_SCALE = RESONANCE_CENTER_BAND_HZ;

/** 시안 §8.8이 말하는 적정 범위(Hz). 판정과 캡션이 **같은 숫자**를 쓴다. */
export const FORMANT_CENTER_GOOD = { min: 2000, max: 4000 };

/**
 * 공명 세기 미터의 눈금(dB). 왼쪽 끝은 네이티브의 **바닥값**이다(계약이 들고 있는
 * `RESONANCE_LEVEL_FLOOR_DB`) — 시안의 `-25`로 자르면 -26dB와 -39dB가 같은 자리에 서서,
 * 서로 다른 두 목소리가 미터에서 구분되지 않는다.
 */
export const RESONANCE_LEVEL_SCALE = { min: RESONANCE_LEVEL_FLOOR_DB, max: 0 };

/** 시안 §8.8이 말하는 적정 범위(dB). */
export const RESONANCE_LEVEL_GOOD = { min: -20, max: -10 };

/**
 * 값 → 미터 위 위치(0..1). 눈금 밖은 **끝에 세운다** — 막대 밖으로 나간 마커는 위치가
 * 아니라 오류로 보인다. 판정이 아니라 배치만 하는 함수라 등급은 `tabInsight`가 낸다.
 */
export const meterPosition = (
  value: number,
  scale: { min: number; max: number },
): number => {
  const ratio = (value - scale.min) / (scale.max - scale.min);
  return clampNum(ratio, 0, 1);
};

/**
 * 적정 범위(값) → 미터 위 구간(위치). 값과 같은 자를 통과하므로 캡션이 말하는 범위와
 * 색칠된 구간이 어긋날 수 없다 — 두 곳에서 따로 곱하면 그 어긋남이 조용히 생긴다.
 */
export const goodBand = (
  good: { min: number; max: number },
  scale: { min: number; max: number },
): { from: number; to: number } => ({
  from: meterPosition(good.min, scale),
  to: meterPosition(good.max, scale),
});

/**
 * 목소리 밝기 미터의 **눈금**(Hz). 시안 §8.9는 `어두움 ↔ 밝음`이라고만 쓰고 숫자를 안 줘서,
 * 눈금은 **네이티브가 실제로 내는 값의 범위**에서 가져왔다.
 *
 * 근거(미보정): 커밋된 데모 4개(`apps/admin/public/samples/m1_*.wav`, 44.1kHz)를 pitch-core의
 * `SpectralShapeEstimator`(2048/1024)로 직접 돌려 유성 프레임(`rms >= 0.25*peak`)의 p5~p95를
 * 잰 값이 밝기 `168~2278 Hz`, 넓이 `217~1136 Hz`였다. 그 폭을 바깥쪽 어림수로 반올림한 것이
 * 아래 둘이다. 잰 신호는 앱과 같다 — 분석기가 `SpectralShapeEstimator`에 넣는 것은 전처리된
 * 창(DC 제거 + `min_frequency_hz`=65 하이패스)이라 측정도 같은 전처리를 통과시켰다.
 * 낮은 쪽 끝(밝기 ~210Hz)은 게이트를 3배 조여도, DC를 걷어내도 그대로여서 무음이나 오프셋이
 * 아니라 그 테이크가 실제로 어두운 것이다 — 눈금 하한을 그 위로 올리지 않은 이유다.
 *
 * **표본은 남성 한 명의 4테이크**라 사람 전체를 대표하지 않는다 — 그래서 두 축 모두
 * `미보정` 배지를 달고, 눈금 밖 값은 `meterPosition`이 끝에 세운다(잘못이 아니라 한계다).
 * 코퍼스가 넓어지면 이 두 상수를 다시 재서 옮긴다.
 */
export const BRIGHTNESS_SCALE = { min: 150, max: 2500 };
/** 울림 넓이(대역폭) 미터의 눈금(Hz). 근거는 `BRIGHTNESS_SCALE` 주석과 같은 측정이다. */
export const BANDWIDTH_SCALE = { min: 200, max: 1300 };

/** 밝기 미터 위 위치(0..1). 0 = 어두움, 1 = 밝음 — 시안 §8.9의 축 방향 그대로다. */
export const brightnessPosition = (brightnessHz: number): number =>
  meterPosition(brightnessHz, BRIGHTNESS_SCALE);

/**
 * 울림 넓이 미터 위 위치(0..1). **뒤집혀 있다** — 0 = 넓음, 1 = 집중.
 *
 * 대역폭은 넓을수록 큰 수인데 시안 §8.9의 축 라벨은 `넓은 울림 ←` / `→ 집중된 울림`이라
 * 그대로 쓰면 그림이 라벨과 반대로 그려진다. 뒤집기를 여기 한 군데에 두는 이유: 슬라이더와
 * 산점도가 같은 함수를 통과해야 두 그림이 서로 다른 방향을 가리키는 일이 없다.
 */
export const spreadPosition = (bandwidthHz: number): number =>
  1 - meterPosition(bandwidthHz, BANDWIDTH_SCALE);

/** 미터 위 다섯 칸의 이름 — 밝기·넓이 배지(TestFlight #59: 세 칸 → 다섯 칸). */
export type PositionStep = 'veryLow' | 'low' | 'mid' | 'high' | 'veryHigh';

/** 칸 이름을 왼쪽부터. 칸 수가 곧 슬라이더·지도의 칸 수다. */
export const POSITION_STEPS: readonly PositionStep[] = [
  'veryLow',
  'low',
  'mid',
  'high',
  'veryHigh',
];

/**
 * 미터 위 위치 → 다섯 칸 배지(시안 §8.9의 `밝은 편` / `균형적` 사이를 세분). 눈금을 오등분한
 * 것뿐이라 **판정이 아니라 위치의 이름**이다 — 눈금 자체가 미보정이라(데모 4테이크) 등급이라고
 * 부르지 않는다. 스케일은 그대로 두고 칸만 나눴다.
 */
export const stepOfPosition = (position: number): PositionStep =>
  POSITION_STEPS[
    clampNum(
      Math.floor(position * POSITION_STEPS.length),
      0,
      POSITION_STEPS.length - 1,
    )
  ];

/** 미터를 삼등분하는 문턱(위치). 눈금이 아니라 자리라 값이 아니라 비율이다. */
const POSITION_THIRDS = { mid: 1 / 3, high: 2 / 3 };

/**
 * 세 칸 이름 — 한 줄 해석이 쓴다(문장은 "어두운 편 / 균형 / 밝은 편"만큼만 말한다). 배지는
 * 다섯 칸(`stepOfPosition`)으로 세분했지만 해석의 문턱은 옮기지 않았다(#59는 표기 세분이다).
 * 두 자리는 방향이 늘 같다 — 삼등분 문턱(1/3·2/3)은 다섯 칸의 가장자리 칸(0.2 아래·0.8 위)을
 * 넘지 않으므로 "어두운 편" 배지 옆에 "밝다"는 문장이 서지 않는다.
 */
export const levelOfPosition = (position: number): QualitativeLevel =>
  levelOf(position, POSITION_THIRDS);

/**
 * 공명 스타일 산점도의 점(시안 §8.9 2×2). `x` = 넓이(0 넓음 ~ 1 집중),
 * `y` = 밝기(0 어두움 ~ 1 밝음). SVG의 아래로 자라는 y 변환은 그리는 쪽이 한다.
 *
 * **두 축이 다 있어야 찍는다.** 한 축을 가운데로 메운 점은 그 사람의 위치가 아닌데,
 * 화면에서는 잰 값과 구별되지 않는다.
 */
export const resonanceStylePoint = (
  record: DiagnosisRecord,
): { x: number; y: number } | null => {
  const brightness = record.brightnessHz;
  const bandwidth = record.bandwidthHz;
  if (brightness == null || bandwidth == null) {
    return null;
  }
  return { x: spreadPosition(bandwidth), y: brightnessPosition(brightness) };
};

/**
 * 0~100 점수 → 세 칸 배지(시안 §8.3·§8.7의 `좋음`/`보통`/`주의`, 호흡의 `안정적`).
 *
 * **미보정 문턱**이다(80/60) — 시안이 92점을 `안정적`, 32점을 `주의`로 그린 것에 맞춰
 * 삼등분에 가깝게 잡았을 뿐 검증된 경계가 아니다. 등급이 아니라 배지 색을 고르는 자리다.
 */
export const SCORE_LEVEL = { high: 80, mid: 60 };

export const scoreLevel = (score: number): QualitativeLevel =>
  levelOf(score, SCORE_LEVEL);

/**
 * 탭마다 한 줄 해석에 쓸 **키 조각**. 문구는 i18n이 갖는다.
 *
 * 규칙은 하나뿐이다: **가장 낮은 값을 지목한다.** 잘된 걸 칭찬하는 문장은 어느 회차에나
 * 붙일 수 있어 정보가 없지만, 어디가 약했는지는 다음에 무엇을 연습할지를 정한다.
 * 말할 근거가 없으면 `null` — 카드 자체가 안 그려진다(빈 칸이 문장 자리에 남지 않는다).
 */
/** 점수 붙은 항목 중 **가장 낮은 것**의 id. 없으면 `null`이다(할 말이 없다). */
const worstId = (items: { id: string; score: number }[]): string | null =>
  items.length === 0
    ? null
    : items.reduce((worst, item) => (item.score < worst.score ? item : worst))
        .id;

/**
 * 탭 → 그 탭의 해석. **표로 두는 이유**: 탭이 하나 늘면 여기 항목도 늘어야 컴파일된다
 * (`Record<MetricTabId, ...>`). if 사슬이었을 때는 마지막 탭이 else 자리라, 새 탭이
 * 조용히 남의 문장을 물려받았다.
 */
const TAB_INSIGHT: Record<
  MetricTabId,
  (record: DiagnosisRecord) => string | null
> = {
  // 목소리 피로도는 **엔진이 재는 값이 없다**(플랜 RISK). 축이 전부 미측정이라
  // 해석할 것도 없다 — 값이 생기는 날 문장도 같이 생긴다.
  fatigue: () => null,
  // 음역은 사실(최저·최고음)이지 점수가 아니라 "낮다/높다"로 짚을 축이 없다.
  range: () => null,
  // 탭 점수와 **같은 규칙**이다(`vibratoAxisScore` = 두 참고 구간 중 더 벗어난 쪽). 속도만
  // 보면 5.5Hz · ±271 cents 회차가 주의 배지 옆에서 "속도가 자연스럽다"고 말한다(#70).
  vibrato: (record) => {
    const vibrato = record.vibrato;
    if (vibrato == null) {
      return null;
    }
    const { rate, depth } = vibratoBandScores(vibrato);
    if (rate === 100 && depth === 100) {
      return 'inBand';
    }
    if (depth < rate) {
      return vibrato.depthCents < VIBRATO_DEPTH_REFERENCE_CENTS.min
        ? 'narrow'
        : 'wide';
    }
    return vibrato.rateHz < VIBRATO_REFERENCE_HZ.min ? 'slow' : 'fast';
  },

  formant: (record) => {
    // 중심 주파수를 먼저 본다: 세기는 마이크·거리로 통째로 밀릴 수 있지만, 에너지가
    // 어느 대역에 모였는지는 그 목소리가 한 일이라 다음에 무엇을 연습할지를 정한다.
    const center = record.resonanceCenterHz;
    if (center != null && center < FORMANT_CENTER_GOOD.min) {
      return 'centerLow';
    }
    if (center != null && center > FORMANT_CENTER_GOOD.max) {
      return 'centerHigh';
    }
    const levelDb = record.resonanceLevelDb;
    if (levelDb != null && levelDb < RESONANCE_LEVEL_GOOD.min) {
      return 'levelLow';
    }
    // 둘 다 없으면 할 말이 없다 — 안 잰 축에 "적정 범위예요"라고 말하지 않는다.
    return center == null && levelDb == null ? null : 'inBand';
  },

  resonance: (record) => {
    // 넓이를 먼저 본다: 시안 §8.9가 유일하게 주는 조언 문장("조금 더 넓은 울림을")이
    // 넓이 축의 것이고, 밝기는 밝다·어둡다에 좋고 나쁨이 없어 할 말이 서술뿐이다.
    const bandwidth = record.bandwidthHz;
    if (bandwidth != null) {
      const spread = levelOfPosition(spreadPosition(bandwidth));
      if (spread === 'high') {
        return 'concentrated';
      }
      if (spread === 'low') {
        return 'wide';
      }
    }
    const brightness = record.brightnessHz;
    if (brightness == null) {
      // 넓이가 가운데뿐이면 할 말이 없다 — 남은 문장(`balanced`)은 "밝기도 넓이도
      // 가운데"라고 두 축을 다 말하는데, 밝기는 안 잰 회차다. 안 잰 축을 두고
      // 가운데라고 하지 않는다(`resonanceStylePoint`가 점을 안 찍는 것과 같은 이유).
      return null;
    }
    const tone = levelOfPosition(brightnessPosition(brightness));
    return tone === 'low' ? 'dark' : tone === 'high' ? 'bright' : 'balanced';
  },

  breath: (record) => worstId(record.breath ?? []),

  registers: (record) =>
    worstId(
      (record.pitchRegisters ?? []).filter(
        (r): r is typeof r & { score: number } => r.score != null,
      ),
    ),
};

export const tabInsight = (
  record: DiagnosisRecord,
  tab: MetricTabId,
): string | null => TAB_INSIGHT[tab](record);

/**
 * 구간 안정성의 **직전 회차 대비**(%). 100이면 그대로, 120이면 20% 좋아진 것이다.
 *
 * 기준선이 직전 회차인 이유: 화면 문구가 `자기 이력 대비`로 **단수**다. 전체 평균을 쓰면
 * 회차가 쌓일수록 기준선이 무뎌져, 오늘 눈에 띄게 좋아져도 숫자가 거의 안 움직인다.
 *
 * `null`인 경우가 둘이고 둘 다 "비교할 것이 없다"다 — 이전 회차가 없거나(첫 회차),
 * 그 회차에 이 구간을 안 불렀거나. 0%로 떨어뜨리지 않는다: 0%는 "다 잃었다"로 읽힌다.
 */
export const registerHistoryPct = (
  current: number,
  previous: DiagnosisRecord | null,
  id: string,
): number | null => {
  const before = previous?.pitchRegisters?.find((r) => r.id === id)?.score;
  return before == null || before === 0
    ? null
    : Math.round((current / before) * 100);
};
